import { motion } from "framer-motion";
import { HeartHandshake } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const STORIES = [
  { src: "/video/story-1.mp4", poster: "/video/story-1-poster.jpg" },
  { src: "/video/story-2.mp4", poster: "/video/story-2-poster.jpg" },
  { src: "/video/story-3.mp4", poster: "/video/story-3-poster.jpg" },
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7 } };

function StoryVideo({ src, poster, index }) {
  return (
    <div className="rounded-[1.6rem] p-[2px] bg-[linear-gradient(140deg,#C9A96A_0%,#6e5522_45%,#C9A96A_65%,#8a6b2f_100%)] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.75)]">
      <div className="rounded-[calc(1.6rem-2px)] overflow-hidden bg-[#1d0810]">
        <video controls preload="none" playsInline poster={poster} data-testid={index === 0 ? "patient-story-video" : `patient-story-video-${index + 1}`} className="w-full h-auto block">
          <source src={src} type="video/mp4" />
        </video>
      </div>
    </div>
  );
}

export function PatientStory() {
  const { t } = useLang();

  const header = (
    <>
      <div className="flex items-center gap-3 mb-5">
        <span className="h-px w-10 bg-[#C9A96A]/60" />
        <span className="flex items-center gap-2.5 text-[11px] font-heading font-bold tracking-[0.18em] sm:tracking-[0.3em] uppercase text-[#C9A96A]">
          <HeartHandshake size={15} /> {t("story_kicker")}
        </span>
      </div>
      <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#F5EFE6] text-balance">{t("story_title")}</h2>
      <p className="mt-5 text-lg text-[#E8DCC8]/70 leading-relaxed">{t("story_sub")}</p>
    </>
  );

  const sectionCls = "relative overflow-hidden py-24 sm:py-32 bg-[linear-gradient(155deg,#16040a_0%,#341120_52%,#16040a_100%)]";
  const kanji = (
    <>
      <span aria-hidden className="absolute -left-8 top-8 font-magserif text-[20vw] leading-none text-[#C9A96A] opacity-[0.06] select-none pointer-events-none hidden lg:block">絆</span>
      <span aria-hidden className="absolute -right-10 bottom-6 font-magserif text-[16vw] leading-none text-[#C9A96A] opacity-[0.05] select-none pointer-events-none hidden lg:block">希望</span>
    </>
  );

  if (STORIES.length === 1) {
    return (
      <section id="hikayeler" className={sectionCls} data-testid="patient-story-section">
        {kanji}
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="lg:col-span-3">
            <StoryVideo {...STORIES[0]} index={0} />
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-2">
            {header}
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="hikayeler" className={sectionCls} data-testid="patient-story-section">
      {kanji}
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-12">
          {header}
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {STORIES.map((s, i) => (
            <motion.div key={s.src} {...fadeUp} transition={{ duration: 0.7, delay: i * 0.08 }}>
              <StoryVideo {...s} index={i} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
